/**
 * Strategy RENAMES, available to any component without prop drilling.
 *
 * A strategy is stored and fetched under its DATA name ("MW2"), but an admin can
 * give it a display name in Strategy Settings ("Kairos Futures"). The server
 * returns that mapping with the strategy list, as `sheetLabels` on /api/sheets.
 *
 * Only two components ever received it — the Results tabs and the Results
 * comparison table — so a rename showed up there and nowhere else. The Portfolio
 * Simulator, Blowout Risk, % Rule Risk, Bulk Risk, CSV Upload and the dashboard
 * widgets all kept rendering the raw name, which reads as a rename that only
 * half-saved.
 *
 * Threading one more prop through six component trees would fix today's call
 * sites and quietly miss tomorrow's. A module store keyed off the same fetch
 * means `resolveStrategyDisplayName` — which those components ALREADY call —
 * just starts returning the right answer.
 *
 * Same shape as utils/activeEntity.js: written once when the data arrives, read
 * synchronously anywhere. It needs no subscription because the labels land in
 * the same response as the strategy list, so the render that shows a new name is
 * the same render that shows a new list.
 */

let labels = {};

/** Replace the rename map. Called when /api/sheets responds. */
export function setStrategyLabels(map) {
  labels = (map && typeof map === 'object') ? map : {};
}

/**
 * The display name for a strategy, or null when it has no rename.
 * Matching is case-insensitive: the map is keyed by the data name as stored,
 * and callers pass whatever the API handed them.
 */
export function labelForStrategy(name) {
  const raw = String(name || '').trim();
  if (!raw) return null;
  if (labels[raw]) return labels[raw];
  const lower = raw.toLowerCase();
  const hit = Object.keys(labels).find((k) => String(k).trim().toLowerCase() === lower);
  return hit ? labels[hit] : null;
}

/** Test seam — lets a test reset module state between cases. */
export function _clearStrategyLabels() {
  labels = {};
}
